"use client";

import IconPoi from "@/components/icon/IconPoi";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { poiCategories, PoiCategory } from "@/db/definitions";
import { useNearViewport } from "@/lib/hook/use-near-viewport";
import { DistanceMatrixData, PoiInfoItem } from "@/lib/types/poi";
import { useQuery } from "@tanstack/react-query";
import { Bike, Bus, Car, Footprints } from "lucide-react";
import { Variants, AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import React, { useState } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  geohash7: string;
};

const formatDistance = (distanceM: number) => {
  return `${(distanceM / 1000).toFixed(1)} km`;
};

const formatTime = (timeS: number) => {
  return `${Math.round(timeS / 60)} min`;
};

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.25, ease: "easeOut" },
  },
};

const allCategories = ["ALL", ...poiCategories] as const;

const PoiGridItems: React.FC<Props> = ({ geohash7 }) => {
  const posthog = usePostHog();
  const t = useTranslations("poi");
  const [category, setCategory] = useState<PoiCategory | "ALL">("ALL");
  const [containerRef, isNearViewport] = useNearViewport<HTMLDivElement>();

  const { data, isLoading } = useQuery({
    queryKey: ["listing", "poi-info", geohash7, category],
    enabled: isNearViewport,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("geohash7", geohash7);

      if (category !== "ALL") {
        params.append("category", category);
      }

      const response = await fetch(
        `/api/site/public/listing/poi-info?${params.toString()}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        return [];
      }

      const data = (await response.json()) as {
        success: boolean;
        data: PoiInfoItem[];
        cacheHit: boolean;
      };
      if (!data.success) {
        return [];
      }

      posthog.capture("poi_fetch", {
        geohash7,
        category: category,
        cache: data.cacheHit,
      });

      return data.data;
    },
  });

  return (
    <div ref={containerRef} className="flex flex-col gap-2 pt-2">
      <div className="flex flex-row gap-x-3 gap-y-2 flex-wrap">
        {allCategories.map((cat) => (
          <Button
            key={cat}
            variant={category === cat ? "default" : "outline"}
            className="text-base"
            onClick={() => {
              setCategory(cat as PoiCategory | "ALL");
              posthog.capture("poi_category_filter", {
                category: cat,
              });
            }}
          >
            {t(`category.${cat}`)}
          </Button>
        ))}
      </div>

      <AnimatePresence>
        {data?.length === 0 && !isLoading && (
          <motion.p
            key="no-data"
            className="text-muted-foreground italic text-center mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {t("radius-info.not-available")}
          </motion.p>
        )}
      </AnimatePresence>

      {((data && data.length > 0) || isLoading) && (
        <motion.div
          key={`poi-items-${category}-${isLoading}`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 pt-4"
        >
          {isLoading ? (
            <>
              {Array.from({ length: 8 }).map((_, index) => (
                <motion.div
                  key={`poi-skeleton-${index}-${category}`}
                  variants={itemVariants}
                >
                  <Skeleton className="w-full h-52 rounded-md" />
                </motion.div>
              ))}
            </>
          ) : (
            <>
              {data?.map((poi) => (
                <motion.div
                  key={`${poi.type}-${poi.id}-${category}`}
                  variants={itemVariants}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-card px-5 py-5 h-52 flex flex-col gap-2 items-center rounded-lg shadow-sm border border-border"
                >
                  <div className="flex flex-row gap-3 items-center w-full">
                    <div className="bg-secondary flex items-center justify-center w-12 h-12 min-w-12 rounded-md">
                      <IconPoi
                        type={poi.type}
                        className="w-6 h-6 text-foreground"
                      />
                    </div>
                    <p className="line-clamp-2 text-card-foreground text-base font-bold">
                      {poi.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm">
                      <span className="text-muted-foreground uppercase">
                        {t(`type.${poi.type}`, {
                          count: 1,
                        })}
                        {" • "}
                      </span>
                      <span className="text-accent font-bold">
                        {formatDistance(poi.distance)}
                      </span>
                    </p>
                  </div>
                  <MatrixGrid data={poi.matrix} />
                </motion.div>
              ))}
            </>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default PoiGridItems;

const MatrixGrid = ({ data }: { data: DistanceMatrixData }) => {
  const hasOnlyWalk =
    !data.bikeDistance &&
    !data.busDistance &&
    !data.driveDistance &&
    !data.bikeTime &&
    !data.busTime &&
    !data.driveTime;
  return (
    <div className="grid grid-cols-2 gap-2 w-full">
      <div className={twMerge("col-span-1", hasOnlyWalk && "col-span-2")}>
        <MatrixItem
          distance={data.walkDistance}
          time={data.walkTime}
          type="walk"
        />
      </div>
      {!hasOnlyWalk && (
        <>
          <MatrixItem
            distance={data.bikeDistance!}
            time={data.bikeTime!}
            type="bike"
          />
          <MatrixItem
            distance={data.busDistance!}
            time={data.busTime!}
            type="bus"
          />
          <MatrixItem
            distance={data.driveDistance!}
            time={data.driveTime!}
            type="drive"
          />
        </>
      )}
    </div>
  );
};

type MatrixItemProps = {
  distance: number;
  time: number;
  type: "walk" | "bike" | "bus" | "drive";
};

const icons: Record<string, React.ReactNode> = {
  walk: <Footprints className="w-6 h-6" />,
  bike: <Bike className="w-6 h-6" />,
  bus: <Bus className="w-6 h-6" />,
  drive: <Car className="w-6 h-6" />,
};

const MatrixItem: React.FC<MatrixItemProps> = ({ distance, time, type }) => {
  return (
    <div className="w-full bg-background justify-center rounded-xl border text-muted-foreground border-input flex flex-row gap-3 items-center px-1 py-1 text-xs">
      {icons[type]}
      <div className="flex flex-col gap-1">
        <p className="text-foreground font-semibold">{formatTime(time)}</p>
        <p className="-mt-1.5">{formatDistance(distance)}</p>
      </div>
    </div>
  );
};
